import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import MyInfoContext from 'contexts/MyInfoContext';
import useExaOneAccess from 'hooks/useExaOneAccess';
import { getCachedExaOneAccess } from 'services/exaOneAccessCache';
import { Link } from 'react-router-dom';
import { all_click_datalayer_push, getRelatedContentsGAProps } from 'utils/GAUtils';
import { dateYYYYMMDD, imageUrl, videoTimeFormat } from 'utils/utils';
import paths from '../../../commons/paths';
import { ContentsDetailType } from '../../../types/enum/ContentsDetailType';
import ExaoneChat, { MOBILE_MAX_WIDTH } from './ExaoneChat';

const EXAONE_EXTRA_WIDTH = 20;

interface RelationContentsProps {
  related: MediaPlaylistResDto[];
  cateType: 'VIDEO' | 'NEWS' | 'GROUP' | 'CARD' | undefined;
  hasExaone?: boolean;
  isExaoneHidden?: boolean;
}

const RelationContents = (props: RelationContentsProps) => {
  const { isLogin, userId } = useContext(MyInfoContext);
  const { isExaoneHidden = false } = props;
  const hasExaone = useExaOneAccess(props.hasExaone !== false);
  const [isExaoneOpen, setIsExaoneOpen] = useState(false);
  const [exaoneStyle, setExaoneStyle] = useState<React.CSSProperties>();
  const titleRef = useRef<HTMLDivElement | null>(null);
  const openSessionRef = useRef<{ userId: string; refreshToken: string | null }>();
  const [accessNotice, setAccessNotice] = useState<{ userId: string; refreshToken: string | null }>();
  const showAccessNotice = Boolean(accessNotice && isLogin && props.hasExaone !== false
    && accessNotice.userId === userId
    && accessNotice.refreshToken === localStorage.getItem('llRefreshToken'));

  useEffect(() => {
    if (hasExaone) {
      setAccessNotice(undefined);
      openSessionRef.current = isExaoneOpen
        ? { userId, refreshToken: localStorage.getItem('llRefreshToken') } : undefined;
      return;
    }
    const session = openSessionRef.current;
    openSessionRef.current = undefined;
    if (isExaoneOpen && isLogin && props.hasExaone !== false && session?.userId === userId
      && session.refreshToken === localStorage.getItem('llRefreshToken')
      && getCachedExaOneAccess(userId)?.denied) {
      setAccessNotice(session);
    }
    setIsExaoneOpen(false);
  }, [hasExaone, isExaoneOpen, isLogin, userId, props.hasExaone]);

  useEffect(() => {
    const area = titleRef.current?.closest('.detail_right') as HTMLElement | null;

    if (((!hasExaone || !isExaoneOpen) && !showAccessNotice) || !area) {
      return undefined;
    }

    const updateStyle = () => {
      if (window.innerWidth <= MOBILE_MAX_WIDTH) {
        setExaoneStyle(undefined);
        return;
      }

      const { left, width } = area.getBoundingClientRect();
      setExaoneStyle({ left, width: width + EXAONE_EXTRA_WIDTH });
    };

    updateStyle();

    const observer = new ResizeObserver(updateStyle);
    observer.observe(area);
    window.addEventListener('resize', updateStyle);
    window.addEventListener('scroll', updateStyle);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateStyle);
      window.removeEventListener('scroll', updateStyle);
    };
  }, [hasExaone, isExaoneOpen, showAccessNotice]);

  const getPathFromMediaCardResDto = (mediaData: MediaCardResDto) => {
    if (!mediaData.contentsType)
      return `${paths.getPath('videoDetail').replace(':mediaId', mediaData.mediaId as string)}`;
    const contentsType = mediaData.contentsType.toLowerCase().toString();

    if (contentsType === 'news')
      return `${paths.getPath('newsDetail').replace(':mediaId', mediaData.mediaId as string)}`;
    if (contentsType === 'card') {
      // 갓찌 웹툰
      if (mediaData.contentsDetailType === ContentsDetailType.CARD_COMICS.toString()) {
        return `${paths.getPath('webtoonDetail').replace(':mediaId', mediaData.mediaId as string)}`;
      }
      // 그 외 - 매거진(글/카드 뉴스)
      return `${paths.getPath('cardNewsDetail').replace(':mediaId', mediaData.mediaId as string)}`;
    }
    return `${paths.getPath('videoDetail').replace(':mediaId', mediaData.mediaId as string)}`;
  };

  return (
    <>
      <div className={`detail_right_title${hasExaone ? ' is-exaone' : ''}`} ref={titleRef}>
        연관 콘텐츠
        {hasExaone && (
          <button
            type="button"
            className="exaone_button is-relation is-pc"
            onClick={() => setIsExaoneOpen(!isExaoneOpen)}
          >
            <span className="exaone_button_logo">
              <img src="/assets/images/icon/ico-exaone-logo.png" alt="" />
            </span>
            <span className="exaone_button_text">EXAONE 이용하기</span>
            <img src="/assets/images/icon/ico-arrow-right-white.svg" alt="" width={16} height={16} />
          </button>
        )}
      </div>
      <ul className="detail_relation">
        {props.related.map((contents: MediaPlaylistResDto, idx) => (
          <li key={idx}>
            <Link
              to={getPathFromMediaCardResDto(contents)}
              className="detail_relation_link"
              {...getRelatedContentsGAProps({
                area: '콘텐츠추가탐색',
                contentsType: props.cateType,
                title: contents.title,
                time: contents.videoTotalTime,
                isVideo: Boolean(contents.videoTotalTime),
                category: contents.cateNm,
                belong: '연관콘텐츠',
                maker: contents.creatorNm,
                tags: contents.mediaTag?.slice(1).replace(/#/g, '_'),
                brightcoveId: contents.videoId,
                mediaContentsType: contents.mediaContentsType as string,
              })}
              onClick={e => {
                window.scrollTo(0, 0);
                all_click_datalayer_push(e.currentTarget);
              }}
            >
              <div className="detail_relation_wrap">
                <div className="relation_thumb">
                  <img
                    src={imageUrl(contents.thumbImageUri) || '/assets/images/illust/illust-img-media.png'}
                    alt=""
                    className="relation_thumb_img"
                  />
                  {contents.mediaContentsType === 'VIDEO' && (
                    <span className="card_thumb_time">{videoTimeFormat(contents.videoTotalTime)}</span>
                  )}
                </div>
                <div className="relation_desc">
                  <div className="relation_title">{contents.title}</div>
                  <div className="relation_sub is-pc">
                    {props.cateType !== 'GROUP' && (
                      <ul className="card_record">
                        <li>
                          <img src="/assets/images/icon/ico-views.png" alt="views" />
                          {contents.readCnt}
                        </li>
                        <li>
                          <img src="/assets/images/icon/ico-comments.png" alt="comments" />
                          {contents.commentCnt}
                        </li>
                        <li>
                          <img src="/assets/images/icon/ico-likes.png" alt="likes" />
                          {contents.likeCnt}
                        </li>
                      </ul>
                    )}
                    <div className="relation_sub_date">{dateYYYYMMDD(contents.postDt)}</div>
                  </div>
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
      {hasExaone &&
        ReactDOM.createPortal(
          <>
            {!isExaoneOpen && !isExaoneHidden && (
              <button
                type="button"
                className="exaone_button is-floating is-mobile"
                onClick={() => setIsExaoneOpen(true)}
              >
                <span className="exaone_button_logo">
                  <img src="/assets/images/icon/ico-exaone-logo.png" alt="" />
                </span>
                <span className="exaone_button_text">EXAONE 이용하기</span>
              </button>
            )}
            <ExaoneChat
              key={userId}
              isOpen={isExaoneOpen}
              isHidden={isExaoneHidden}
              onClose={() => setIsExaoneOpen(false)}
              style={exaoneStyle}
            />
          </>,
          document.body
        )}
      {showAccessNotice && ReactDOM.createPortal(
        <section
          className={`exaone_chat${isExaoneHidden ? ' is-hidden' : ''}`}
          style={exaoneStyle}
          aria-label="EXAONE 이용 종료"
        >
          <div className="exaone_chat-header">
            <p className="exaone_chat-title">LG LIFE · EXAONE</p>
          </div>
          <div className="exaone_chat-body is-intro">
            <div className="exaone_chat-intro">
              <div className="exaone_chat-hero" style={{ gap: 24, width: '100%' }}>
                <p role="alert" className="exaone_chat-hero_desc">EXAONE 이용 권한이 없습니다. 이용이 종료됩니다.</p>
                <button type="button" className="exaone_chat-start_button" autoFocus onClick={() => setAccessNotice(undefined)}>
                  확인
                </button>
              </div>
            </div>
          </div>
        </section>,
        document.body
      )}
    </>
  );
};

export default React.memo(RelationContents, (prev, next) => {
  return JSON.stringify(prev) === JSON.stringify(next);
});
